import { _ } from "js/vendor";
import { ASSET_MAX_SIZE, ASSET_MAX_UPLOAD_MEGAPIXELS, ImageErrorType, AssetType, ASSET_FILETYPE, ASSET_FILETYPE_TO_MIMETYPE, ASSET_MIMETYPE_TO_FILETYPE } from "common/constants";
import { loadImage, loadFileAsDataUrl } from "js/core/utilities/promiseHelper";

export async function readImage(file, assetType) {
    if (file.size > ASSET_MAX_SIZE) {
        const error = new Error("Your file size should be under " + ASSET_MAX_SIZE / 1024 / 1024 + "MB. Please reduce your file size and try again!");
        error.type = ImageErrorType.SIZE;
        error.title = "Upload Error";
        throw error;
    }

    const dataUrl = await loadFileAsDataUrl(file);
    let image = await loadImage(dataUrl);

    if (assetType === AssetType.LOGO && file.type !== "image/svg+xml") {
        let updatedDataURL = trimWhiteEdges(image);
        image = await loadImage(updatedDataURL);
    }

    const imageMegapixels = (image.naturalHeight * image.naturalWidth) / (1024 * 1024);
    if (imageMegapixels > ASSET_MAX_UPLOAD_MEGAPIXELS) {
        const error = new Error(`Your file size should be under under 200 megapixels, yours is ${imageMegapixels}. Please reduce your file's aspect ratio and try again!`);
        error.type = ImageErrorType.SIZE;
        error.title = "Upload Error";
        throw error;
    }

    const imageProps = getImageProperties(image, file);

    return {
        //since logos will have an updated url due to trimWhiteEdges we need to create a new blob for the logo
        file: assetType === AssetType.LOGO ? (await convertDataURItoBlob(image.src)) : file,
        dataUrl: image.src,
        imageProps
    };
}

export async function convertDataURItoBlob(dataURI) {
    return await fetch(dataURI).then(resp => resp.blob());
}

export function getImageType(file) {
    const mimeType = file.type?.toLowerCase();
    let fileType = ASSET_MIMETYPE_TO_FILETYPE[mimeType];

    if (fileType) return fileType;

    const extension = file.name?.split(".").pop()?.toLowerCase();
    return ASSET_MIMETYPE_TO_FILETYPE[ASSET_FILETYPE_TO_MIMETYPE[extension]];
}

export function getVideoType(file) {
    let fileType = null;
    let fileName = file.name.toLowerCase();
    if (_.endsWith(fileName, ".mp4")) {
        fileType = ASSET_FILETYPE.MP4;
    } else if (_.endsWith(fileName, ".mov")) {
        fileType = ASSET_FILETYPE.MOV;
    } else if (_.endsWith(fileName, ".wmv")) {
        fileType = ASSET_FILETYPE.WMV;
    } else if (_.endsWith(fileName, ".avi")) {
        fileType = ASSET_FILETYPE.AVI;
    } else if (_.endsWith(fileName, ".mkv")) {
        fileType = ASSET_FILETYPE.MKV;
    } else if (_.endsWith(fileName, ".webm")) {
        fileType = ASSET_FILETYPE.WEBM;
    }
    return fileType;
}

export function isValidImageType(file) {
    return !!getImageType(file);
}

export function isValidVideoType(file) {
    return (
        file.type?.includes("video/") && !!getVideoType(file)
    );
}

export function isValidMediaType(file) {
    let isValidImage = isValidImageType(file);
    let isValidVideo = isValidVideoType(file);
    let isValidMedia = (
        isValidImage ||
        isValidVideo
    );
    return {
        isValidMedia,
        isValidImage,
        isValidVideo,
    };
}

export function toDataURL(img, outputFormat = "image/jpeg") {
    let canvas = document.createElement("canvas");
    let ctx = canvas.getContext("2d");
    let dataURL;
    canvas.height = img.naturalHeight;
    canvas.width = img.naturalWidth;
    ctx.drawImage(img, 0, 0);
    dataURL = canvas.toDataURL(outputFormat);
    return dataURL.replace(/^data:image\/(png|jpg);base64,/, "");
}

export function getAverageImageColor(img) {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(img, 0, 0);

    const imageData = [
        // To save time and resources will go through 6 lines across the quarters of the image
        ...ctx.getImageData(0, img.naturalHeight / 4, img.naturalWidth, 1).data,
        ...ctx.getImageData(0, img.naturalHeight / 2, img.naturalWidth, 1).data,
        ...ctx.getImageData(0, img.naturalHeight / 4 * 3, img.naturalWidth, 1).data,
        ...ctx.getImageData(img.naturalWidth / 4, 0, 1, img.naturalHeight).data,
        ...ctx.getImageData(img.naturalWidth / 2, 0, 1, img.naturalHeight).data,
        ...ctx.getImageData(img.naturalWidth / 4 * 3, 0, 1, img.naturalHeight).data,
    ];

    let r = 0, g = 0, b = 0;
    let hasAlpha = false;
    let countedPixels = 0;
    for (let idx = 0; idx < imageData.length; idx += 4) {
        const a = imageData[idx + 3];
        if (a < 255) {
            hasAlpha = true;
        }
        // Won't count this pixel if it's transparent
        if (a === 0) {
            continue;
        }

        r += imageData[idx];
        g += imageData[idx + 1];
        b += imageData[idx + 2];
        countedPixels++;
    }

    r /= countedPixels;
    g /= countedPixels;
    b /= countedPixels;

    return { r, g, b, hasAlpha };
}

export function getImageProperties(img, file) {
    const fileType = getImageType(file);

    // don't bother checking for alpha and solid background for JPEGs. Yes, there are cases where a JPEG could have a solid background but i think the hit on hi-res JPEGs isn't worth it
    if (fileType == ASSET_FILETYPE.JPG) {
        return {
            width: img.naturalWidth,
            height: img.naturalHeight,
            fileType: fileType,
            hasAlpha: false,
            hasSolidBackground: false,
            size: file.size
        };
    }

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");

    // draw image
    ctx.drawImage(img, 0, 0);

    const updateHistogram = (hist, val, ii) => {
        hist[ii % 4] = hist[ii % 4] || {};
        hist[ii % 4][val] = hist[ii % 4][val] || 0;
        hist[ii % 4][val]++;
        return hist;
    };

    const histogram = [
        ctx.getImageData(0, 0, img.naturalWidth, 1).data,
        ctx.getImageData(0, img.naturalHeight - 1, img.naturalWidth, 1).data,
        ctx.getImageData(0, 0, 1, img.naturalHeight).data,
        ctx.getImageData(img.naturalWidth - 1, 0, 1, img.naturalHeight).data
    ].reduce(
        (hist, arr) => arr.reduce(updateHistogram, hist), {});

    function histSum(fn, hist) {
        return Object.keys(hist).map(val => fn(val) * hist[val]).reduce((l, r) => l + r, 0);
    }

    function histStats(hist) {
        const count = histSum(x => 1, hist);
        const sum = histSum(x => x, hist);
        const avg = sum / count;
        const stddev = Math.sqrt(histSum(x => Math.pow(x - avg, 2), hist) / count);
        return { count, avg, stddev };
    }

    const { stddev, avg } = histStats(histogram[3]);
    // Allow slight deviation from 255 as it sometimes happens
    // with PNG screenshots made on MacOS
    const hasAlpha = !(stddev < 1 && avg > 254);

    const imageBackgroundColor = {
        r: Math.floor(histStats(histogram[0]).avg),
        g: Math.floor(histStats(histogram[1]).avg),
        b: Math.floor(histStats(histogram[2]).avg)
    };

    const hasSolidBackground = [histogram[0], histogram[1], histogram[2]].every(
        hist => histStats(hist).stddev < 15);

    return {
        width: img.naturalWidth,
        height: img.naturalHeight,
        fileType: fileType,
        hasAlpha: hasAlpha,
        hasSolidBackground: hasSolidBackground,
        imageBackgroundColor: imageBackgroundColor,
        size: file.size
    };
}

export function trimWhiteEdges(imageObject) {
    let imgWidth = imageObject.width;
    let imgHeight = imageObject.height;
    let canvas = document.createElement("canvas");
    canvas.setAttribute("width", imgWidth);
    canvas.setAttribute("height", imgHeight);
    let context = canvas.getContext("2d");
    context.drawImage(imageObject, 0, 0);

    let imageData = context.getImageData(0, 0, imgWidth, imgHeight),
        data = imageData.data,
        getRBGA = function(x, y) {
            var offset = imgWidth * y + x;
            return {
                red: data[offset * 4],
                green: data[offset * 4 + 1],
                blue: data[offset * 4 + 2],
                alpha: data[offset * 4 + 3]
            };
        },
        isWhite = function(rgb) {
            if (rgb.alpha == 0) {
                return true;
            } else if (rgb.alpha == 255) {
                // many images contain noise, as the white is not a pure #fff white
                return (rgb.red > 240 && rgb.green > 240 && rgb.blue > 240);
            } else {
                return false;
            }
        },

        scanY = function(fromTop) {
            var offset = fromTop ? 1 : -1;

            // loop through each row
            for (var y = fromTop ? 0 : imgHeight - 1; fromTop ? (y < imgHeight) : (y > -1); y += offset) {
                // loop through each column
                for (var x = 0; x < imgWidth; x++) {
                    var rgba = getRBGA(x, y);
                    if (!isWhite(rgba)) {
                        if (fromTop) {
                            return y;
                        } else {
                            return Math.min(y + 1, imgHeight - 1);
                        }
                    }
                }
            }
            return null; // all image is white
        },
        scanX = function(fromLeft) {
            var offset = fromLeft ? 1 : -1;

            // loop through each column
            for (var x = fromLeft ? 0 : imgWidth - 1; fromLeft ? (x < imgWidth) : (x > -1); x += offset) {
                // loop through each row
                for (var y = 0; y < imgHeight; y++) {
                    var rgba = getRBGA(x, y);
                    if (!isWhite(rgba)) {
                        if (fromLeft) {
                            return x;
                        } else {
                            return Math.min(x + 1, imgWidth - 1);
                        }
                    }
                }
            }
            return null; // all image is white
        };

    let cropTop = scanY(true) - 5,
        cropBottom = scanY(false) + 5,
        cropLeft = scanX(true) - 5,
        cropRight = scanX(false) + 5,
        cropWidth = cropRight - cropLeft,
        cropHeight = cropBottom - cropTop;

    canvas.setAttribute("width", cropWidth);
    canvas.setAttribute("height", cropHeight);
    // finally crop the guy
    canvas.getContext("2d").drawImage(imageObject,
        cropLeft, cropTop, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight);

    return canvas.toDataURL();
}

export async function getImageBrightnessForRegion(img, imageBounds, regionBounds) {
    const canvas = document.createElement("canvas");
    canvas.width = imageBounds.width;
    canvas.height = imageBounds.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const loadImage = path => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous"; // Important for CORS if used with Canvas
            img.src = path;

            img.onload = () => {
                resolve(img); // Resolve the Promise when the image loads
            };

            img.onerror = e => {
                reject(e); // Reject the Promise if there's an error loading the image
            };
        });
    };

    if (typeof img == "string") {
        img = await loadImage(img);
    }

    ctx.drawImage(img, 0, 0, imageBounds.width, imageBounds.height);

    // draw rect of regionBounds for debugging
    ctx.strokeStyle = "red";
    ctx.lineWidth = 5;
    ctx.strokeRect(regionBounds.left, regionBounds.top, regionBounds.width, regionBounds.height);

    // add canvas to DOM to avoid "Tainted canvases may not be exported" error in some browsers
    document.body.appendChild(canvas);
    // set canvas top to 0
    canvas.style.position = "absolute";
    canvas.style.top = "0";
    canvas.style.left = "0px"; // move it out of view
    canvas.style.zIndex = 10000;

    // specify the region you want to measure
    const x = parseInt(regionBounds.left); // left edge of region
    const y = parseInt(regionBounds.top); // top edge of region
    const w = parseInt(regionBounds.width); // width of region
    const h = parseInt(regionBounds.height); // height of region

    return analyzeRegion(ctx, x, y, w, h, 4);
}

// --- WCAG helpers (unchanged) ---
function sRGBtoLinear(c) {
    const cs = c / 255;
    return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}
function relativeLuminanceFromRGB(r, g, b) {
    const R = sRGBtoLinear(r), G = sRGBtoLinear(g), B = sRGBtoLinear(b);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}
function contrastRatio(L1, L2) {
    const light = Math.max(L1, L2), dark = Math.min(L1, L2);
    return (light + 0.05) / (dark + 0.05);
}
function pickTextColorByWCAG(avgR, avgG, avgB, textIsLarge = false) {
    const Lbg = relativeLuminanceFromRGB(avgR, avgG, avgB);
    const cWhite = contrastRatio(1.0, Lbg);
    const cBlack = contrastRatio(Lbg, 0.0);
    const text = cWhite >= cBlack ? "#FFF" : "#000";
    const AA = textIsLarge ? 3.0 : 4.5;
    const AAA = textIsLarge ? 4.5 : 7.0;
    const best = Math.max(cWhite, cBlack);
    return { text, cBlack, cWhite, passesAA: best >= AA, passesAAA: best >= AAA };
}

// --- Utilities ---
function median(arr) {
    if (!arr.length) return 0;
    const a = arr.slice().sort((x, y) => x - y);
    const m = Math.floor(a.length / 2);
    return a.length % 2 ? a[m] : 0.5 * (a[m - 1] + a[m]);
}
function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}

// --- Region analysis with robust + spatially aware "busy-ness" ---
function analyzeRegion(ctx, x, y, w, h, pixelIter = 2, textIsLarge = false) {
    const img = ctx.getImageData(x, y, w, h);
    const { data } = img;
    const stride = w * 4;

    const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722;

    // Sample grid
    const gx = Math.ceil(w / pixelIter);
    const gy = Math.ceil(h / pixelIter);
    const Ygrid = new Array(gy);
    const Rgrid = new Array(gy);
    const Ggrid = new Array(gy);
    const Bgrid = new Array(gy);
    const Ys = [];

    let sumR = 0, sumG = 0, sumB = 0;

    let jj = 0;
    for (let j = 0; j < h; j += pixelIter, jj++) {
        Ygrid[jj] = new Array(gx);
        Rgrid[jj] = new Array(gx);
        Ggrid[jj] = new Array(gx);
        Bgrid[jj] = new Array(gx);

        let ii = 0;
        for (let i = 0; i < w; i += pixelIter, ii++) {
            const idx = j * stride + i * 4;
            const r = data[idx], g = data[idx + 1], b = data[idx + 2];

            const Y = luma(r, g, b);
            Ygrid[jj][ii] = Y;
            Rgrid[jj][ii] = r;
            Ggrid[jj][ii] = g;
            Bgrid[jj][ii] = b;

            Ys.push(Y);
            sumR += r; sumG += g; sumB += b;
        }
    }

    const count = Ys.length;
    const avgY = Ys.reduce((a, b) => a + b, 0) / count;
    const medianY = median(Ys);
    const MAD = median(Ys.map(v => Math.abs(v - medianY)));     // robust spread
    const robustStd = 1.4826 * MAD;                             // ≈ stddev for normal dist
    const coefVarRobust = medianY > 0 ? robustStd / medianY : 0;

    const avgR = sumR / count, avgG = sumG / count, avgB = sumB / count;

    // --- Gradient & spatial coherence ---
    // Compute gradient magnitude (L1) on the sampled grid,
    // then threshold to mark "significant" local contrast.
    const H = gy, W = gx;
    const gradMask = Array.from({ length: H }, () => new Array(W).fill(false));
    let grads = [];
    for (let y0 = 0; y0 < H; y0++) {
        for (let x0 = 0; x0 < W; x0++) {
            const Yc = Ygrid[y0][x0];
            let gRight = 0, gDown = 0;
            if (x0 + 1 < W) gRight = Math.abs(Yc - Ygrid[y0][x0 + 1]);
            if (y0 + 1 < H) gDown = Math.abs(Yc - Ygrid[y0 + 1][x0]);
            const gmag = gRight + gDown; // cheaper than sqrt
            grads.push(gmag);
        }
    }

    // Robust threshold for gradients: relative to local spread, with a floor.
    const medGrad = median(grads);
    const madGrad = median(grads.map(v => Math.abs(v - medGrad)));
    const robustGrad = 1.4826 * madGrad;
    const gradThresh = Math.max(12, medGrad + 1.5 * robustGrad); // tuneable: 12..24 typical

    // Build mask of "strong" gradients
    for (let y0 = 0; y0 < H; y0++) {
        for (let x0 = 0; x0 < W; x0++) {
            const Yc = Ygrid[y0][x0];
            const gRight = x0 + 1 < W ? Math.abs(Yc - Ygrid[y0][x0 + 1]) : 0;
            const gDown = y0 + 1 < H ? Math.abs(Yc - Ygrid[y0 + 1][x0]) : 0;
            const gmag = gRight + gDown;
            gradMask[y0][x0] = gmag > gradThresh;
        }
    }

    // Remove isolated pixels (sparse stars) via a 3x3 neighbor count (morphological opening-ish)
    const prunedMask = Array.from({ length: H }, () => new Array(W).fill(false));
    let edgeCount = 0;
    let clusterCount = 0;
    let continuitySum = 0;

    const neighborDirs = [
        [-1, -1],
        [-1, 0],
        [-1, 1],
        [0, -1],
        [0, 1],
        [1, -1],
        [1, 0],
        [1, 1]
    ];

    for (let y0 = 0; y0 < H; y0++) {
        // Continuity: longest run of trues in the row
        let longestRun = 0, run = 0;

        for (let x0 = 0; x0 < W; x0++) {
            if (gradMask[y0][x0]) {
                edgeCount++;
                let n = 0;
                for (const [dy, dx] of neighborDirs) {
                    const yy = y0 + dy, xx = x0 + dx;
                    if (yy >= 0 && yy < H && xx >= 0 && xx < W && gradMask[yy][xx]) n++;
                }
                // keep only if has >=2 neighbors -> part of a cluster
                if (n >= 2) {
                    prunedMask[y0][x0] = true;
                    clusterCount++;
                }
            }

            // continuity (row-wise runs)
            if (prunedMask[y0][x0]) {
                run++;
                if (run > longestRun) longestRun = run;
            } else {
                run = 0;
            }
        }
        // normalize longest run by row length
        continuitySum += W ? (longestRun / W) : 0;
    }

    const edgeCoverage = count ? (edgeCount / (W * H)) : 0;           // raw strong-edge proportion
    const clusteredCoverage = count ? (clusterCount / (W * H)) : 0;   // after pruning singles
    const continuityScore = H ? (continuitySum / H) : 0;              // 0..1

    // Backward-compatible "edge density" (mean gradient) for reference
    const edgeDensity = grads.reduce((a, b) => a + b, 0) / grads.length;

    // --- WCAG text color decision ---
    const wcag = pickTextColorByWCAG(avgR, avgG, avgB, textIsLarge);
    const failsAA = !wcag.passesAA;

    // --- Busy decision (new heuristics) ---
    // These thresholds bias toward ignoring sparse, tiny features.
    const BUSY_ROBUST_STD = 24;    // robust stddev of luma (≈9% of 255)
    const BUSY_COEFVAR = 0.20;  // relative spread
    const BUSY_EDGE_COV = 0.10;  // ≥10% of cells have strong edges
    const BUSY_CLUSTER_COV = 0.06;  // ≥6% remain after pruning singles
    const BUSY_CONTINUITY = 0.20;  // typical of text/foliage/texture bands

    // Require **coverage + clustering** to call it busy from edges alone.
    const edgeBusy =
        (edgeCoverage >= BUSY_EDGE_COV && clusteredCoverage >= BUSY_CLUSTER_COV) ||
        (clusteredCoverage >= BUSY_CLUSTER_COV && continuityScore >= BUSY_CONTINUITY);

    // Robust luminance spread can also indicate busy backgrounds independently.
    const luminanceBusy =
        robustStd >= BUSY_ROBUST_STD || coefVarRobust >= BUSY_COEFVAR;

    // Optional: midtones are risky for readability even if not busy
    const forceBoxOnMidtones = avgY > 90 && avgY < 165;

    const looksBusy = edgeBusy || luminanceBusy;
    const needsBox = looksBusy || failsAA || forceBoxOnMidtones;

    return {
        // Brightness & color
        avgBrightness: avgY,
        avgColor: { r: avgR, g: avgG, b: avgB },

        // Robust stats
        medianBrightness: medianY,
        madBrightness: MAD,
        robustStd,                 // ≈ robust stddev
        coefVarRobust,

        // Edge/texture signals
        edgeDensity,               // mean gradient (legacy-ish)
        edgeCoverage,              // fraction of strong-edge cells
        clusteredCoverage,         // fraction remaining after pruning singles
        continuityScore,           // 0..1, rowwise run-length coherence

        // WCAG
        wcagContrastWhite: wcag.cWhite,
        wcagContrastBlack: wcag.cBlack,
        textColor: wcag.text,
        wcagPassesAA: wcag.passesAA,
        wcagPassesAAA: wcag.passesAAA,

        // Decisions
        looksBusy,
        needsBox
    };
}
